import { useState, useRef } from 'react';
import { ClipboardList, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUpdateDrillLog } from '@/hooks/coach/use-coach-sessions';

interface DrillSuggestion {
  drill: string;
  count: number;
}

interface DrillLogProps {
  sessionId: string;
  drills: string[];
  suggestions?: DrillSuggestion[];
}

export function DrillLog({ sessionId, drills, suggestions = [] }: DrillLogProps) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync, isPending } = useUpdateDrillLog(sessionId);

  const filteredSuggestions = input.trim()
    ? suggestions
        .filter(
          ({ drill }) =>
            drill.toLowerCase().includes(input.toLowerCase().trim()) &&
            !drills.map(d => d.toLowerCase()).includes(drill.toLowerCase())
        )
        .slice(0, 5)
    : [];

  const handleAdd = async (value?: string) => {
    const trimmed = (value ?? input).trim();
    if (!trimmed) return;
    const updated = [...drills, trimmed];
    setInput('');
    setShowSuggestions(false);
    await mutateAsync(updated);
    inputRef.current?.focus();
  };

  const handleRemove = async (index: number) => {
    const updated = drills.filter((_, i) => i !== index);
    await mutateAsync(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="w-4 h-4" />
          Drill Log
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {drills.length === 0 ? (
          <p className="text-sm text-muted-foreground">No drills logged yet.</p>
        ) : (
          <ol className="space-y-1.5">
            {drills.map((drill, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground w-5 shrink-0 text-right">{i + 1}.</span>
                <span className="flex-1">{drill}</span>
                <button
                  onClick={() => handleRemove(i)}
                  disabled={isPending}
                  className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                  aria-label="Remove drill"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ol>
        )}

        <div className="flex gap-2 pt-1">
          <div className="relative flex-1">
            <Input
              ref={inputRef}
              placeholder="Add a drill..."
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setShowSuggestions(true);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              disabled={isPending}
              className="text-sm"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-md overflow-hidden">
                {filteredSuggestions.map(({ drill }) => (
                  <button
                    key={drill}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAdd(drill);
                    }}
                  >
                    {drill}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => handleAdd()}
            disabled={!input.trim() || isPending}
            className="shrink-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
